import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  IMarketChartContextData,
} from '@/finsight/domain/vo/MarketChart/IMarketChartContextData';
import MarketChartRepository
  from '@/finsight/infrastructure/repository/MarketChartRepository/MarketChartRepository';
import { useDIContext } from '@/Framework/DI/DIContext';
import {
  initialMarketChartResponse,
} from '@/finsight/application/MarketChart/constants';
import {
  filterEmptyDeals,
} from '@/finsight/application/MarketChart/serverSide/helpers';

const useMarketChart = (data) => {
  const [chartData, setChartData] = useState<IMarketChartContextData>(data);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const { container } = useDIContext();
  const marketChartRepository = container.get(MarketChartRepository);

  const getMarketChartData = async (filters) => {
    setIsFetching(true);
    try {
      const { chartItems, showVolume } = await marketChartRepository.getChartData(filters);
      setChartData({
        collection: filterEmptyDeals(chartItems), showVolume,
      });
    } catch (e) {
      setChartData(initialMarketChartResponse);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => setChartData(data), [data]);

  useEffect(() => {
    if (data?.hasServerError) {
      getMarketChartData(data.preparedPayload);
    }
  }, [data]);

  return {
    chartData: { ...chartData, isFetching },
    setChartData,
  };
};

type MarketChartContextType = ReturnType<typeof useMarketChart>;

const MarketChartContext = createContext<MarketChartContextType>(null);

export const useMarketChartContext = () => {
  const context = useContext(MarketChartContext);
  if (!context) {
    throw new Error('useMarketChartContext must be used within the MarketChartContext');
  }
  return context;
};

const MarketChartContextProvider = ({ children, chartData }) => (
  <MarketChartContext.Provider value={ useMarketChart(chartData) }>
    { children }
  </MarketChartContext.Provider>
);

export default MarketChartContextProvider;
